<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <div class="logo">
      <RouterLink to="/" aria-label="Visit home page">
        <img class="logo__img" src="@/assets/logo.png" />
      </RouterLink>
    </div>
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in visibleRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${route.displayName}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="15px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ route.displayName }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              class="sidebar-child-item"
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="childActiveColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${route.displayName}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ childRoute.displayName }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<style lang="scss" scoped>
.logo {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;

  &__img {
    height: 28px;
  }
}
</style>
<script lang="ts">
import { defineComponent, watch, ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'

import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useUserStore } from '../../stores/user-store'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const userStore = useUserStore()

    const visibleRoutes = computed(() =>
      navigationRoutes.routes.filter((r) => !r.meta.roles || r.meta.roles.includes(userStore.role)),
    )

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    // Detail pages aren't in the nav config themselves, but should still count as "within"
    // their list page's nav entry — e.g. /persons/:id should keep "Sales" active/expanded.
    const relatedRouteNames: Record<string, string[]> = {
      persons: ['person-detail'],
      'qa-persons': ['qa-person-detail'],
    }

    const matchesRouteName = (name: string) =>
      route.name === name || (relatedRouteNames[name]?.includes(route.name as string) ?? false)

    const isActiveChildRoute = (child: INavigationRoute) => matchesRouteName(child.name)

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return matchesRouteName(section.name)
      }

      return section.children.some(({ name }) => matchesRouteName(name))
    }

    const setActiveExpand = () =>
      (value.value = visibleRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '230px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => '#003DA6')
    // Lighter shade for child nav items so a nested section reads as a step down from its parent.
    const childActiveColor = computed(() => '#00B3FF')

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? '#fff' : '#2A3256')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? '#fff' : '#2A3256')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })
    // Re-apply once children have registered with the accordion — belt-and-braces against any
    // mount-order race between this initial computation and VaCollapse's own item registration.
    onMounted(() => nextTick(setActiveExpand))

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      childActiveColor,
      navigationRoutes,
      visibleRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
